/**
 * FASE 7.1 — Chiusura giornata guidata
 *
 * Orchestrator page: reads live state from backend, allows the user to jump
 * to each module (Vendite / Prodotti / Finanze / HACCP / Operatività), and
 * finally SIGN the day. NEVER writes to underlying operational data.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, fmtEUR, fmtInt } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  CheckCircle2, AlertTriangle, XCircle, MinusCircle, ChevronDown, ChevronUp,
  Signature, Unlock, ArrowRight, Info, RefreshCw, Calendar as CalIcon,
} from "lucide-react";

const todayIso = () => new Date().toISOString().slice(0, 10);
const MIN_DATE = () => {
  const d = new Date(); d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
};

const STATUS_META = {
  COMPLETE: { icon: CheckCircle2, cls: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", label: "Completo" },
  WARNING:  { icon: AlertTriangle, cls: "text-amber-700",  bg: "bg-amber-50 border-amber-200",   label: "Da verificare" },
  MISSING:  { icon: XCircle,       cls: "text-rose-700",   bg: "bg-rose-50 border-rose-200",     label: "Mancante" },
  "N/A":    { icon: MinusCircle,   cls: "text-stone-500",  bg: "bg-stone-50 border-stone-200",   label: "Non applicabile" },
};

const STEP_META = {
  vendite:     { title: "1. Vendite",              subtitle: "Chiusura confermata e letture cassa", href: "/sales",              obligatorio: true },
  prodotti:    { title: "2. Performance Prodotti", subtitle: "Tracking manuale della giornata",     href: "/product-performance", obligatorio: false },
  finanze:     { title: "3. Finanze",              subtitle: "Movimenti e liquidità",               href: "/finance",             obligatorio: false },
  haccp:       { title: "4. HACCP",                subtitle: "Checklist obbligatorie",              href: "/haccp",               obligatorio: false },
  operativita: { title: "5. Operatività",          subtitle: "Task, ordini, alert",                 href: "/tasks",               obligatorio: false },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META["N/A"];
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${meta.bg} ${meta.cls}`}>
      <Icon size={12} strokeWidth={2} /> {meta.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// STEP CARDS
// ---------------------------------------------------------------------------
function StepCard({ stepKey, step, businessDate, financeAck, onFinanceAckChange, expanded, onToggle }) {
  const meta = STEP_META[stepKey];
  const status = step.status;
  const s = STATUS_META[status] || STATUS_META["N/A"];
  return (
    <div className={`border rounded-lg bg-white overflow-hidden ${s.bg}`} data-testid={`step-${stepKey}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/60"
        data-testid={`step-${stepKey}-toggle`}
      >
        <div className="flex items-center gap-3">
          <StatusBadge status={status} />
          <div className="text-left">
            <div className="font-medium">{meta.title} {meta.obligatorio && <span className="text-[10px] uppercase text-neutral-500 ml-1">obbligatorio</span>}</div>
            <div className="text-xs text-neutral-500">{meta.subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {(step.warnings || []).length > 0 && (
            <span className="text-xs text-amber-700 hidden sm:inline">
              {step.warnings.length} avviso{step.warnings.length > 1 ? "" : ""}
            </span>
          )}
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>
      {expanded && (
        <div className="border-t px-4 py-3 space-y-3 bg-white">
          <StepDetails stepKey={stepKey} step={step} financeAck={financeAck}
            onFinanceAckChange={onFinanceAckChange} businessDate={businessDate} />
          {(step.warnings || []).length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs">
              <div className="font-medium text-amber-900 mb-1">Avvisi</div>
              <ul className="list-disc list-inside space-y-0.5 text-amber-900">
                {step.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          <div className="flex justify-end">
            <Link to={meta.href} data-testid={`step-${stepKey}-cta`}>
              <Button size="sm" variant="outline">
                Vai al modulo <ArrowRight size={12} className="ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function StepDetails({ stepKey, step, financeAck, onFinanceAckChange }) {
  const d = step.details || {};
  if (stepKey === "vendite") {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Metric label="Fatturato confermato" value={d.confirmed_revenue == null ? "—" : fmtEUR(d.confirmed_revenue)} />
        <Metric label="Scontrini" value={d.receipt_count == null ? "—" : fmtInt(d.receipt_count)} />
        <Metric label="Contanti" value={d.cash_amount == null ? "—" : fmtEUR(d.cash_amount)} />
        <Metric label="POS" value={d.card_amount == null ? "—" : fmtEUR(d.card_amount)} />
        <Metric label="Altri" value={d.other_amount == null ? "—" : fmtEUR(d.other_amount)} />
        <Metric label="Ultima lettura" value={d.reading_cumulative == null ? "—" : `${fmtEUR(d.reading_cumulative)} (${d.reading_time || "—"})${d.reading_partial ? " · parziale" : ""}`} />
        <Metric label="Delta lettura vs chiusura" value={d.delta_confirmed_minus_reading == null ? "—" : `${d.delta_confirmed_minus_reading > 0 ? "+" : ""}${fmtEUR(d.delta_confirmed_minus_reading)}`} />
        <Metric label="Rif. fiscale" value={d.fiscal_closure_ref || "—"} />
      </div>
    );
  }
  if (stepKey === "prodotti") {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Metric label="Stato batch" value={d.batch_status || "—"} />
        <Metric label="Prodotti tracciati" value={fmtInt(d.products_tracked || 0)} />
        <Metric label="Pezzi totali" value={fmtInt(d.total_quantity || 0)} />
        <Metric label="Ricavo teorico" value={fmtEUR(d.tracked_revenue || 0)} />
        <Metric label="Chiusura confermata" value={d.confirmed_revenue == null ? "—" : fmtEUR(d.confirmed_revenue)} />
        <Metric label="Copertura %" value={d.coverage_pct == null ? "—" : `${d.coverage_pct}%`} />
      </div>
    );
  }
  if (stepKey === "finanze") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Metric label="Movimenti" value={fmtInt(d.movements_count || 0)} />
          <Metric label="Entrate operative" value={fmtEUR(d.operating_in || 0)} />
          <Metric label="Uscite operative" value={fmtEUR(d.operating_out || 0)} />
          <Metric label="Liquidità totale" value={fmtEUR(d.total_liquidity || 0)} />
        </div>
        <div className="rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900 flex items-start gap-2">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>{d.info || "Il fatturato non coincide con i movimenti finanziari."}</span>
        </div>
        <label className="flex items-center gap-2 text-sm" data-testid="finance-ack">
          <input type="checkbox" checked={financeAck}
            onChange={(e) => onFinanceAckChange(e.target.checked)}
            data-testid="finance-ack-checkbox" />
          <span>Ho verificato i movimenti finanziari della giornata (presa visione, non riconcilia)</span>
        </label>
        {(d.transactions || []).length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-neutral-600">Vedi movimenti ({d.transactions.length})</summary>
            <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
              {d.transactions.map((t, i) => (
                <li key={t.id || i} className="flex justify-between border-b border-stone-100 py-1">
                  <span>{t.type === "in" ? "▲" : "▼"} <span className="text-neutral-500">{t.category}</span> — {t.description || "—"}</span>
                  <span className="tabular-nums">{fmtEUR(t.amount)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    );
  }
  if (stepKey === "haccp") {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-3 text-sm">
          <Metric label="Checklist richieste" value={d.required || 0} />
          <Metric label="Completate" value={d.completed || 0} />
          <Metric label="Anomalie" value={d.anomalies_total || 0} />
        </div>
        {(d.checklists || []).length > 0 && (
          <ul className="text-xs space-y-1">
            {d.checklists.map((c) => (
              <li key={c.template_id} className="flex justify-between">
                <span>{c.completed ? "✓" : "✗"} {c.name}</span>
                <span className="text-neutral-500">{c.signed_by || "—"}{c.anomalies_count ? ` · ${c.anomalies_count} anomalie` : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  if (stepKey === "operativita") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Metric label="Task aperte/scadute" value={d.open_tasks_count || 0} />
          <Metric label="Ordini in ritardo" value={d.late_orders_count || 0} />
        </div>
        {step.critical_flag && (
          <div className="rounded border border-rose-300 bg-rose-50 p-2 text-xs text-rose-900 font-medium">
            ⚠ CRITICAL: presenti task ad alta priorità aperte
          </div>
        )}
        {(d.open_tasks || []).length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-neutral-600">Task aperte ({d.open_tasks.length})</summary>
            <ul className="mt-2 space-y-1">
              {d.open_tasks.map((t) => (
                <li key={t.id}>{t.date} · [{t.priority || "media"}] {t.title}</li>
              ))}
            </ul>
          </details>
        )}
        {(d.late_orders || []).length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-neutral-600">Ordini in ritardo ({d.late_orders.length})</summary>
            <ul className="mt-2 space-y-1">
              {d.late_orders.map((o) => (
                <li key={o.id}>{o.supplier_name} · prev. {o.expected_date} · {o.status}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
    );
  }
  return null;
}

function Metric({ label, value }) {
  return (
    <div className="border border-stone-200 rounded p-2 bg-stone-50">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-sm font-medium tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SIGNATURE & REOPEN DIALOGS
// ---------------------------------------------------------------------------
function SignDialog({ open, onOpenChange, state, businessDate, financeAck, onSigned }) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) setNotes(""); }, [open]);
  const warnings = useMemo(() => {
    if (!state) return [];
    return Object.entries(state.steps || {}).flatMap(([k, s]) =>
      (s.warnings || []).map((w) => `[${k}] ${w}`));
  }, [state]);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/daily-close/sign", {
        business_date: businessDate, notes: notes || null,
        acknowledged_finance: !!financeAck,
      });
      toast.success("Giornata firmata");
      onOpenChange(false);
      onSigned && onSigned(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "object" ? (detail.message || "Errore firma") : (detail || "Errore firma"));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sign-dialog">
        <DialogHeader><DialogTitle>Firma chiusura giornata · {businessDate}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm">
            Stai firmando la chiusura della giornata <b>{businessDate}</b>.
          </p>
          {warnings.length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
              <div className="font-medium mb-1">
                Ci sono {warnings.length} elementi da verificare:
              </div>
              <ul className="list-disc list-inside space-y-0.5 max-h-32 overflow-y-auto">
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          <div>
            <Label>Note di chiusura (opzionale)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Es. serata regolare, 2 anomalie HACCP"
              maxLength={500}
              data-testid="sign-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="sign-submit">
            <Signature size={14} className="mr-1" />
            {saving ? "Firma in corso…" : "Conferma firma"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReopenDialog({ open, onOpenChange, businessDate, onReopened }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) setReason(""); }, [open]);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/daily-close/${businessDate}/reopen`,
        { reason: reason || null });
      toast.success("Giornata riaperta");
      onOpenChange(false);
      onReopened && onReopened(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "object" ? (detail.message || "Errore") : (detail || "Errore riapertura"));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reopen-dialog">
        <DialogHeader><DialogTitle>Riapri giornata · {businessDate}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm">La riapertura NON modifica vendite, tracking, finanze, HACCP, task o ordini. Revoca solo lo stato di firma.</p>
          <div>
            <Label>Motivo (opzionale)</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Es. errore nella chiusura vendite, da correggere"
              maxLength={500}
              data-testid="reopen-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="reopen-submit">
            <Unlock size={14} className="mr-1" />
            {saving ? "…" : "Conferma riapertura"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// MAIN PAGE
// ---------------------------------------------------------------------------
export default function DailyClose() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [businessDate, setBusinessDate] = useState(params.get("date") || todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({ vendite: true });
  const [financeAck, setFinanceAck] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);

  // FASE 7.4 STEP E — no more role hardcoding.
  const canSign = can("daily_close.sign");
  const canReopen = can("daily_close.reopen");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/daily-close/${businessDate}`);
      setData(data);
      // preserve existing acknowledged state from signature snapshot
      const ack = data?.signature?.step_snapshot?.finanze?.acknowledged;
      setFinanceAck(!!ack);
    } catch (e) {
      toast.error("Errore caricamento chiusura giornata");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessDate]);

  const state = data?.current_state;
  const signature = data?.signature;
  const hasSignature = !!(signature && signature.status === "signed");
  const isReopened = !!(signature && signature.status === "reopened");

  const toggle = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));

  const isToday = businessDate === todayIso();

  return (
    <div data-testid="daily-close-page" className="space-y-4">
      <PageHeader
        eyebrow="Fine giornata"
        title="Chiusura giornata guidata"
        subtitle="Verifica i 5 controlli chiave e firma la giornata"
        actions={
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label className="text-xs">Data</Label>
              <Input type="date" value={businessDate}
                min={MIN_DATE()} max={todayIso()}
                onChange={(e) => setBusinessDate(e.target.value)}
                data-testid="date-selector"
                className="w-40" />
            </div>
            <Button size="sm" variant="outline" onClick={load} data-testid="refresh-btn">
              <RefreshCw size={12} className="mr-1" /> Aggiorna
            </Button>
          </div>
        }
      />

      {!isToday && (
        <div className="rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900 flex items-center gap-2">
          <CalIcon size={12} /> Consultazione retrospettiva — stai analizzando una giornata precedente
        </div>
      )}

      {loading || !state ? (
        <div className="text-sm text-neutral-500">Caricamento…</div>
      ) : (
        <>
          {/* Signature status banner */}
          {hasSignature && (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 flex justify-between items-center flex-wrap gap-2" data-testid="signature-banner">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={20} className="text-emerald-700" />
                <div>
                  <div className="font-medium text-emerald-900">Giornata firmata</div>
                  <div className="text-xs text-emerald-800">
                    Firmata da <b>{signature.signed_by}</b> il {new Date(signature.signed_at).toLocaleString("it-IT")} · {signature.completion_score}
                  </div>
                  {signature.notes && <div className="text-xs text-emerald-800 mt-1">Note: {signature.notes}</div>}
                </div>
              </div>
              {canReopen && (
                <Button size="sm" variant="outline" onClick={() => setReopenOpen(true)} data-testid="reopen-btn">
                  <Unlock size={12} className="mr-1" /> Riapri giornata
                </Button>
              )}
            </div>
          )}
          {isReopened && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="reopened-banner">
              <div className="font-medium text-amber-900">Giornata riaperta</div>
              <div className="text-xs text-amber-800">
                Riaperta da <b>{signature.reopened_by}</b> il {new Date(signature.reopened_at).toLocaleString("it-IT")} · Precedente firma di {signature.signed_by}
              </div>
              {signature.reason && <div className="text-xs text-amber-800 mt-1">Motivo: {signature.reason}</div>}
            </div>
          )}

          {/* Progress bar */}
          <ProgressBar completion={state.completion} />

          {/* 5 STEP CARDS */}
          <div className="space-y-3">
            {["vendite", "prodotti", "finanze", "haccp", "operativita"].map((k) => (
              <StepCard key={k} stepKey={k} step={state.steps[k]}
                businessDate={businessDate}
                financeAck={financeAck}
                onFinanceAckChange={(v) => setFinanceAck(v)}
                expanded={!!expanded[k]} onToggle={() => toggle(k)} />
            ))}
          </div>

          {/* Final action */}
          {!hasSignature && (
            <div className="border border-stone-300 rounded-lg p-4 bg-white sticky bottom-2" data-testid="final-action">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-medium">Firma la chiusura</div>
                  <div className="text-xs text-neutral-500">
                    {state.can_sign
                      ? "Step 1 (Vendite) completo — puoi firmare la giornata."
                      : "Manca la chiusura vendite: firma non disponibile."}
                  </div>
                </div>
                {canSign ? (
                  <Button
                    onClick={() => setSignOpen(true)}
                    disabled={!state.can_sign}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="sign-btn"
                    title={!state.can_sign ? "Inserisci prima la chiusura vendite" : undefined}
                  >
                    <Signature size={14} className="mr-1" /> Firma chiusura giornata
                  </Button>
                ) : (
                  <span className="text-xs text-neutral-500">Solo admin o manager possono firmare</span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <SignDialog open={signOpen} onOpenChange={setSignOpen}
        state={state} businessDate={businessDate}
        financeAck={financeAck} onSigned={() => load()} />
      <ReopenDialog open={reopenOpen} onOpenChange={setReopenOpen}
        businessDate={businessDate} onReopened={() => load()} />
    </div>
  );
}

function ProgressBar({ completion }) {
  if (!completion) return null;
  const pct = Math.round((completion.complete / completion.total) * 100);
  return (
    <div className="border border-stone-200 rounded-lg p-3 bg-white" data-testid="progress-bar">
      <div className="flex justify-between items-baseline mb-2">
        <div className="label-eyebrow">Completamento</div>
        <div className="text-sm font-medium">{completion.label} controlli completi ({pct}%)</div>
      </div>
      <div className="w-full h-2 bg-stone-100 rounded overflow-hidden">
        <div className={`h-full transition-all ${pct === 100 ? "bg-emerald-600" : "bg-amber-500"}`}
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
